import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  LinearProgress,
} from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useDistanceCalculationStatus } from '@/api/endpoints/vendor/useDistanceCalculationStatus';
import { useGetSearchHistory } from '@/api/endpoints/vendor/useGetSearchHistory';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { ClockIcon } from '@/assets/icons/ClockIcon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { SearchLgIcon } from '@/assets/icons/SearchLgIcon';
import { PMPRoutes } from '@/config/routes';

// Distance Status Component for Search Cards
const DistanceCalculationIndicator: React.FC<{ searchId: string }> = ({ searchId }) => {
  const { data: distanceStatus } = useDistanceCalculationStatus(searchId, true);

  if (!distanceStatus) return null;

  if (distanceStatus.calculation_complete) {
    return (
      <Chip
        label="Distances calculated"
        size="small"
        color="success"
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    );
  }

  if (distanceStatus.pending_calculations > 0) {
    const progress = (distanceStatus.calculated_distances / distanceStatus.total_vendors) * 100;
    
    return (
      <Box sx={{ minWidth: 120 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'info.main', fontSize: '0.7rem' }}>
            Calculating distances
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {distanceStatus.calculated_distances}/{distanceStatus.total_vendors}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            borderRadius: 2,
            bgcolor: 'info.100',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'info.main',
            },
          }}
        />
      </Box>
    );
  }

  return null;
};

const VendorSearchHistoryPage: React.FC = () => {
  const history = useHistory();
  const { data: searchHistory, isLoading, error } = useGetSearchHistory();

  const handleBackToSearch = () => {
    history.push(PMPRoutes.vendorSearch);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 3 } }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          {/* Header */}
          <Box>
            <Button
              startIcon={<ArrowLeftIcon width={20} height={20} />}
              onClick={handleBackToSearch}
              sx={{ mb: 2 }}
            >
              Back to Search
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Your Search History
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>
              View and repeat your previous vendor searches
            </Typography>
          </Box>

          {/* Content */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error">Failed to load search history. Please try again later.</Alert>
          )}

          {!isLoading && !error && searchHistory && searchHistory.length === 0 && (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <SearchLgIcon width={48} height={48} style={{ marginBottom: 16, color: '#9CA3AF' }} />
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                No Search History
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Your vendor searches will appear here
              </Typography>
              <Button
                variant="contained"
                onClick={handleBackToSearch}
                startIcon={<SearchLgIcon width={20} height={20} />}
              >
                Start New Search
              </Button>
            </Paper>
          )}

          {!isLoading && !error && searchHistory && searchHistory.length > 0 && (
            <Grid container spacing={3}>
              {searchHistory.map(search => (
                <Grid item xs={12} md={6} key={search.id}>
                  <Card
                    sx={{
                      height: '100%',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={2}>
                        {/* Search Query */}
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              mb: 0.5,
                            }}
                          >
                            {search.search_query}
                          </Typography>
                          {search.processed_query &&
                            search.processed_query !== search.search_query && (
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                            >
                                Searched as: {search.processed_query}
                            </Typography>
                          )}
                        </Box>

                        {/* Address */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MarkerPin01Icon width={16} height={16} style={{ color: '#6B7280' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {search.address}
                          </Typography>
                        </Box>

                        {/* Date and Results */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ClockIcon width={16} height={16} style={{ color: '#6B7280' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {search.created_at
                                ? (() => {
                                  try {
                                    const date = new Date(search.created_at);
                                    return Number.isNaN(date.getTime())
                                      ? 'Date unavailable'
                                      : format(date, 'MMM d, yyyy h:mm a');
                                  } catch {
                                    return 'Date unavailable';
                                  }
                                })()
                                : 'Date unavailable'}
                            </Typography>
                          </Box>
                          <Stack direction="column" spacing={0.5} alignItems="flex-end">
                            <Chip
                              label={`${search.vendor_count} vendors`}
                              size="small"
                              color={search.vendor_count > 0 ? 'success' : 'default'}
                              variant="outlined"
                            />
                            <DistanceCalculationIndicator searchId={search.id} />
                          </Stack>
                        </Box>

                        {/* Top Vendors Preview */}
                        {search.vendors && search.vendors.length > 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', mb: 1, display: 'block' }}
                            >
                              Top Vendors Found:
                            </Typography>
                            <Stack spacing={0.5}>
                              {search.vendors.slice(0, 3).map((vendor, index) => (
                                <Box
                                  key={vendor.id}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.primary',
                                      fontWeight: 500,
                                      flexGrow: 1,
                                    }}
                                  >
                                    {index + 1}. {vendor.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{ color: 'text.secondary' }}
                                    >
                                      {vendor.distance_formatted || vendor.distance}
                                    </Typography>
                                    {vendor.distance_meters !== null && (
                                      <Box
                                        sx={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          bgcolor: 'success.main',
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              ))}
                              {search.vendors.length > 3 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                                >
                                  +{search.vendors.length - 3} more vendors
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          history.push(`/property-manager/vendor-search-history/${search.id}`, {
                            search,
                          })
                        }
                        sx={{ flex: 1 }}
                      >
                        View Details
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default VendorSearchHistoryPage;
